import { keyframes } from '@emotion/react'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { ArrowIcon } from '../../../../components/icons'
import { TestParameter } from '../../../../types/TestParameter'
import { FloatingBox } from './FloatingBox'

const TEST_STRIP_BOXES = Array.from({ length: 16 }, (_, i) => i + 1)

const breatheAnimation = keyframes`
    0% {
        transform: translateY(-2px);
        box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);

    }
    50% {
        transform: translateY(-4px);
        box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);

    }
    100% {
        transform: translateY(-2px);
        box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);

    }
`

interface ManualUploadHeaderProps {
  selectedValues: Record<string, number>
  parameters: TestParameter[]
}

export const ManualUploadHeader = ({ selectedValues, parameters }: ManualUploadHeaderProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const getBoxColor = (boxIndex: number) => {
    const parameter = parameters[boxIndex - 1]
    if (!parameter) return '#E6EEF4'

    const selectedValue = selectedValues[parameter.name]
    if (selectedValue === undefined) return '#E6EEF4'

    const valueObj = parameter.values.find((v) => v.value === selectedValue)
    return valueObj ? valueObj.color : '#E6EEF4'
  }
  const getColorSet = (number: number) => {
    const selectValue = selectedValues[parameters[number - 1]?.name] !== undefined

    return {
      color: selectValue ? 'white' : theme.palette.text.primary,
      boxShadow: selectValue ? '0px 2px 4px rgba(0, 0, 0, 0.15)' : 'none',
      transform: selectValue ? 'translateY(-2px)' : 'none',
      animation: !selectValue ? `${breatheAnimation} 2s ease-in-out infinite` : 'none',
      cursor: selectValue ? 'pointer' : 'default',
      filter: selectValue ? 'brightness(0.95)' : 'none',
      transformHover: selectValue ? 'translateY(-3px)' : 'none',
      boxShadowHover: selectValue ? '0px 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
    }
  }

  const handleBoxClick = (boxIndex: number) => {
    const parameter = parameters[boxIndex - 1]
    if (!parameter || selectedValues[parameter.name] !== undefined) return

    const element = document.getElementById(`parameter-${boxIndex}`)
    if (element) {
      const headerOffset = isMobile ? 120 : 150 // Adjust these values based on your header height
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        backgroundColor: 'background.default',
        zIndex: 1,
        py: { xs: 1.5, md: 2 },
        mx: { xs: -2, md: 0 },
        px: { xs: 2, md: 0 },
      }}
    >
      <Stack spacing={1}>
        {isMobile && (
          <Typography
            color="text.primary"
            fontSize="12px"
            fontWeight={600}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            Start here <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />
          </Typography>
        )}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            '&::after': isMobile
              ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  height: '100%',
                  width: '40px',
                  background: 'linear-gradient(to left, rgba(255,255,255,1), rgba(255,255,255,0))',
                  pointerEvents: 'none',
                }
              : {},
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: 'flex-start', md: 'center' }}
          >
            {!isMobile && (
              <Typography
                color="text.primary"
                fontSize="14px"
                fontWeight={600}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  minWidth: 'auto',
                  gap: '7px',
                  mr: 1,
                }}
              >
                Start here <ArrowIcon />
              </Typography>
            )}
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                backgroundColor: 'white',
                p: 1,
                pr: { xs: 4, md: 12 },
                borderRadius: 1,
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                width: '100%',
              }}
            >
              {TEST_STRIP_BOXES.map((number) => (
                <FloatingBox
                  key={number}
                  number={number}
                  boxColor={getBoxColor(number)}
                  colorSet={getColorSet(number)}
                  onClick={handleBoxClick}
                />
              ))}
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Box>
  )
}
